import { useForm } from '@uhg-abyss/web/hooks/useForm';
import { styled } from '@uhg-abyss/web/tools/styled';
import { createTheme } from '@uhg-abyss/web/tools/theme';
import { Button } from '@uhg-abyss/web/ui/Button';
import { FormProvider } from '@uhg-abyss/web/ui/FormProvider';
import { Heading } from '@uhg-abyss/web/ui/Heading';
import { ThemeProvider } from '@uhg-abyss/web/ui/ThemeProvider';
import copy from 'copy-to-clipboard';
import React, { useEffect, useState } from 'react';

import { Locale } from '../../../ProviderSearch/context/Internationalization/types';
import {
  BANNER_DEEPLINK_FORM_MESSAGE,
  BASE_URLS,
  BTN_COPIED,
  BTN_COPY,
  BTN_GENERATE,
  BTN_OPEN,
  BTN_RESET,
  ENV_DEV,
  ENV_INT,
  ENV_LOCAL,
  ENV_PROD,
  ENV_STAGE,
  ENV_UAT,
  ERROR_CLIENT_NAME_REQUIRED,
  ERROR_COVERAGE_TYPE_REQUIRED,
  ERROR_LANGUAGE_REQUIRED,
  ERROR_LOB_REQUIRED,
  ERROR_MNR_RECIPROCITY_ID_REQUIRED,
  ERROR_PLAN_NAME_REQUIRED,
  ERROR_RECIPROCITY_ID_REQUIRED,
  ERROR_VALIDATION_SUMMARY,
  GUEST_SUFFIX,
  HEADING_GENERATED_DEEPLINK_URLS,
  HEADING_GUEST_DEEPLINK_GENERATOR,
  LABEL_CLIENT_NAME,
  LABEL_CLUSTER_KEY,
  LABEL_COVERAGE_TYPE,
  LABEL_CUSTOM_BRANDING,
  LABEL_FIPS_CODE,
  LABEL_LANGUAGE,
  LABEL_LOB,
  LABEL_MNR_RECIPROCITY_ID,
  LABEL_PLAN_NAME,
  LABEL_PLAN_YEAR,
  LABEL_POLICY_NUMBER,
  LABEL_PORTAL,
  LABEL_PV_RC,
  LABEL_RECIPROCITY_ID,
  LABEL_SAVE_WITHOUT_VALIDATION,
  LABEL_SHOW_SIGN_IN,
  LABEL_STATE_CODE,
  OPTION_BURMESE,
  OPTION_COVERAGE_BEHAVIORAL,
  OPTION_COVERAGE_DENTAL,
  OPTION_COVERAGE_MEDICAL,
  OPTION_COVERAGE_PHARMACY,
  OPTION_COVERAGE_VISION,
  OPTION_ENGLISH,
  OPTION_HAITIAN_CREOLE,
  OPTION_ILOCANO,
  OPTION_KOREAN,
  OPTION_LOB_CS,
  OPTION_LOB_EI,
  OPTION_LOB_IFP,
  OPTION_LOB_MR,
  OPTION_LOB_SHOP,
  OPTION_NO,
  OPTION_SPANISH,
  OPTION_TRADITIONAL_CHINESE,
  OPTION_VIETNAMESE,
  OPTION_YES,
  PLACEHOLDER_CLIENT_NAME,
  PLACEHOLDER_CLUSTER_KEY,
  PLACEHOLDER_FIPS_CODE,
  PLACEHOLDER_MNR_RECIPROCITY_ID,
  PLACEHOLDER_PLAN_NAME,
  PLACEHOLDER_PLAN_YEAR,
  PLACEHOLDER_POLICY_NUMBER,
  PLACEHOLDER_PORTAL,
  PLACEHOLDER_PV_RC,
  PLACEHOLDER_RECIPROCITY_ID,
  PLACEHOLDER_SELECT,
  PLACEHOLDER_SELECT_COVERAGE_TYPE,
  PLACEHOLDER_SELECT_LANGUAGE,
  PLACEHOLDER_SELECT_LOB,
  PLACEHOLDER_STATE_CODE,
  PORTAL_GEHA,
  PORTAL_UMR,
  TITLE_DEEPLINK_FORM,
  VALUE_DISABLE,
  VALUE_ENABLE,
  VALUE_NO,
  VALUE_YES,
} from './constants';
import {
  ButtonContainer,
  CopyButton,
  DeeplinkFormDemoProviderSearchContainer,
  DeeplinkFormStyledSelectInput,
  DeeplinkFormStyledTextInput,
  StyledButton,
  UrlDisplayContainer,
  UrlLabel,
  UrlRow,
  UrlText,
} from './GuestTokenForm.styled';
import { InstructionsTable } from './InstructionsTable';
import { DeeplinksBanner, SampleDeeplinkTable } from './SampleDeeplinkTable';

const MainContainer = styled('div', {
  margin: '24px 24px 48px 24px',
  padding: '0 24px',
  '@media (max-width: 984px)': {
    padding: 0,
  },
});

const PageContainer = styled('div', {
  display: 'grid',
  gridTemplateColumns: '1fr 2fr',
  gap: '24px',
  alignItems: 'start',
  '@media (max-width: 984px)': {
    gridTemplateColumns: '1fr',
    gap: '16px',
  },
});

const InstructionsSection = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  border: '1px solid $core.color.neutral.20',
  borderRadius: '$md',
  padding: '20px',
});

const Section2Container = styled('div', {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '24px',
  alignItems: 'start',
  '@media (max-width: 744px)': {
    gridTemplateColumns: '1fr',
    gap: '16px',
  },
});

const FormSection = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
  padding: '20px 24px',
  border: '1px solid $core.color.neutral.20',
  borderRadius: '$md',
  '@media (max-width: 984px)': {
    padding: '20px 16px',
  },
});

const ExampleSection = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  border: '1px solid $core.color.neutral.20',
  borderRadius: '$md',
  padding: '20px',
});

const GeneratedUrlsWrapper = styled('div', {
  gridColumn: '1 / 3',
  '@media (max-width: 744px)': {
    gridColumn: '1',
  },
});

const ValidationSummary = styled('div', {
  marginTop: '12px',
  marginBottom: '8px',
  padding: '8px 12px',
  backgroundColor: '#fef2f2',
  border: '1px solid #fecaca',
  borderRadius: '$sm',
  color: '#d32f2f',
  fontSize: '14px',
  fontWeight: 500,
});

interface GeneratedUrls {
  [ENV_LOCAL]: string;
  [ENV_DEV]: string;
  [ENV_INT]: string;
  [ENV_STAGE]: string;
  [ENV_UAT]: string;
  [ENV_PROD]: string;
}

// Language options based on requirements
const LANGUAGE_OPTIONS = [
  { value: Locale.enUS, label: OPTION_ENGLISH },
  { value: Locale.esUS, label: OPTION_SPANISH },
  { value: Locale.koUS, label: OPTION_KOREAN },
  { value: Locale.viVN, label: OPTION_VIETNAMESE },
  { value: Locale.zhTW, label: OPTION_TRADITIONAL_CHINESE },
  { value: Locale.ilPH, label: OPTION_ILOCANO },
  { value: Locale.myMM, label: OPTION_BURMESE },
  { value: Locale.htHT, label: OPTION_HAITIAN_CREOLE },
];

// LOB options
const LOB_OPTIONS = [
  { value: '', label: PLACEHOLDER_SELECT_LOB },
  { value: 'EI', label: OPTION_LOB_EI },
  { value: 'MR', label: OPTION_LOB_MR },
  { value: 'CS', label: OPTION_LOB_CS },
  { value: 'IFP', label: OPTION_LOB_IFP },
  { value: 'SHOP', label: OPTION_LOB_SHOP },
];

// Coverage Type options
const COVERAGE_TYPE_OPTIONS = [
  { value: '', label: PLACEHOLDER_SELECT_COVERAGE_TYPE },
  { value: 'M', label: OPTION_COVERAGE_MEDICAL },
  { value: 'B', label: OPTION_COVERAGE_BEHAVIORAL },
  { value: 'D', label: OPTION_COVERAGE_DENTAL },
  { value: 'V', label: OPTION_COVERAGE_VISION },
  { value: 'P', label: OPTION_COVERAGE_PHARMACY },
];

// Custom Branding and Show Sign-in options
const YES_NO_OPTIONS = [
  { value: VALUE_YES, label: OPTION_YES },
  { value: VALUE_NO, label: OPTION_NO },
];

const ENABLE_DISABLE_OPTIONS = [
  { value: '', label: '-' },
  { value: VALUE_ENABLE, label: OPTION_YES },
  { value: VALUE_DISABLE, label: OPTION_NO },
];

export const DeeplinkGeneratorForm = () => {
  const form = useForm({
    mode: 'onChange', // Validate on change to give instant feedback
  });

  const [generatedUrls, setGeneratedUrls] = useState<GeneratedUrls | null>(
    null
  );
  const [copiedField, setCopiedField] = useState<string | null>(null);

  // Set default language on mount
  useEffect(() => {
    form.setValue('language', 'en');
  }, []);

  // Clear generated URLs when there are validation errors
  useEffect(() => {
    const hasErrors = Object.keys(form.formState.errors || {}).length > 0;
    if (hasErrors && generatedUrls) {
      setGeneratedUrls(null);
    }
  }, [form.formState.errors, generatedUrls]);

  // Watch form values for conditional logic
  const watchedValues = form.watch();

  // Clear conditional validation errors when conditions are no longer met
  useEffect(() => {
    const { customBranding } = watchedValues;

    // Clear clientName error when customBranding is not 'yes'
    if (customBranding !== VALUE_YES && form.formState.errors.clientName) {
      form.clearErrors('clientName');
    }
  }, [watchedValues.customBranding]);

  useEffect(() => {
    const {
      reciprocityId,
      clientName,
      planName,
      policyNumber,
      fipsCode,
      clusterKey,
      pvrc,
    } = watchedValues;

    // Clear lob error when ALL triggering fields are empty
    const hasAnyLobTrigger = !!(
      reciprocityId ||
      clientName ||
      planName ||
      policyNumber ||
      fipsCode ||
      clusterKey ||
      pvrc
    );

    if (!hasAnyLobTrigger && form.formState.errors.lob) {
      form.clearErrors('lob');
    }
  }, [
    watchedValues.reciprocityId,
    watchedValues.clientName,
    watchedValues.planName,
    watchedValues.policyNumber,
    watchedValues.fipsCode,
    watchedValues.clusterKey,
    watchedValues.pvrc,
  ]);

  useEffect(() => {
    const { planName } = watchedValues;

    // Clear coverageType and reciprocityId errors when planName is empty
    const hasPlanName = !!planName;

    if (!hasPlanName) {
      if (form.formState.errors.coverageType) {
        form.clearErrors('coverageType');
      }
      if (form.formState.errors.reciprocityId) {
        form.clearErrors('reciprocityId');
      }
      if (form.formState.errors.mnrReciprocityId) {
        form.clearErrors('mnrReciprocityId');
      }
    }
  }, [watchedValues.planName]);

  // Clear mnrReciprocityId error when LOB/coverage is not MNR Medical
  useEffect(() => {
    const { lob, coverageType } = watchedValues;

    if (
      !(lob === 'MR' && coverageType === 'M') &&
      form.formState.errors.mnrReciprocityId
    ) {
      form.clearErrors('mnrReciprocityId');
    }
  }, [watchedValues.lob, watchedValues.coverageType]);

  // Clear planName error when reciprocityId is empty
  useEffect(() => {
    const { reciprocityId } = watchedValues;

    if (!reciprocityId && form.formState.errors.planName) {
      form.clearErrors('planName');
    }
  }, [watchedValues.reciprocityId]);

  // Helper function to check if LOB should be required
  const isLobRequired = () => {
    const {
      reciprocityId,
      clientName,
      planName,
      policyNumber,
      fipsCode,
      clusterKey,
      pvrc,
    } = watchedValues;

    return !!(
      reciprocityId ||
      clientName ||
      planName ||
      policyNumber ||
      fipsCode ||
      clusterKey ||
      pvrc
    );
  };

  // Helper function to check if planName field is populated
  // Both Coverage Type and Reciprocity ID are required when planName has a value
  const isPlanFieldPopulated = () => {
    const { planName } = watchedValues;
    return !!planName;
  };

  const isCoverageTypeRequired = isPlanFieldPopulated;
  const isReciprocityIdRequired = isPlanFieldPopulated;

  // MNR Reciprocity ID only applies to MNR LOB with Medical coverage
  const isMnrLobSelected = () => {
    const { lob, coverageType } = watchedValues;
    return lob === 'MR' && coverageType === 'M';
  };

  // MNR Reciprocity ID is required alongside the H/PBP code for MNR Medical deeplinks
  const isMnrReciprocityIdRequired = () =>
    isMnrLobSelected() && isReciprocityIdRequired();

  // Helper function to check if Client Name should be required
  const isClientNameRequired = () => {
    const { customBranding } = watchedValues;
    return customBranding === VALUE_YES;
  };

  // Helper function to check if Plan Name should be required
  // Plan Name is required when Reciprocity ID/Network ID/Plan ID or H/PBP Code is populated
  const isPlanNameRequired = () => {
    const { reciprocityId } = watchedValues;
    return !!reciprocityId;
  };

  const handleGenerate = async () => {
    // Trigger form validation explicitly
    const isValid = await form.trigger();
    if (!isValid) {
      // Validation failed, errors will be shown automatically
      return;
    }

    const values = form.getValues();
    setGeneratedUrls(null);

    // Build the deeplink parameters object
    const deeplinkParams: Record<string, string> = {
      language: values.language,
    };

    // Add conditional and optional fields if they have values
    if (values.lob) deeplinkParams.lob = values.lob;
    if (values.coverageType) deeplinkParams.coverageType = values.coverageType;
    if (values.reciprocityId)
      deeplinkParams.reciprocityId = values.reciprocityId;
    if (
      values.lob === 'MR' &&
      values.coverageType === 'M' &&
      values.mnrReciprocityId
    )
      deeplinkParams.mnrReciprocityId = values.mnrReciprocityId;
    if (values.clientName) deeplinkParams.clientName = values.clientName;
    if (values.planName) deeplinkParams.planName = values.planName;
    if (values.customBranding)
      deeplinkParams.customBranding = values.customBranding;
    if (values.policyNumber) deeplinkParams.policyNumber = values.policyNumber;
    if (values.fipsCode) deeplinkParams.fipsCode = values.fipsCode;
    if (values.clusterKey) deeplinkParams.clusterKey = values.clusterKey;
    if (values.portal) {
      deeplinkParams.portal = values.portal;
    } else if (values.signin === VALUE_DISABLE) {
      deeplinkParams.portal = 'psx';
    }
    if (values.pvrc) deeplinkParams.pvrc = values.pvrc;
    if (values.saveWithoutPlanValidation === VALUE_YES) {
      deeplinkParams.saveWithoutPlanValidation = 'true';
    }
    if (values.signin) deeplinkParams.signin = values.signin;
    if (values.stateCode) deeplinkParams.stateCode = values.stateCode;

    // Create base64 encoded string using Buffer (consistent with codebase standards)
    const paramsString = JSON.stringify(deeplinkParams);
    const encodedParams = Buffer.from(paramsString).toString('base64');

    // Helper function to get base URL (convert to tpa.com for GEHA/UMR portal sources)
    const getBaseUrl = (env: string): string => {
      const baseUrl = BASE_URLS[env];
      const portal = values.portal?.toUpperCase();
      if (portal === PORTAL_GEHA || portal === PORTAL_UMR) {
        return baseUrl.replace('.uhc.com', '.tpa.com');
      }

      return baseUrl;
    };

    // Generate URLs for all environments
    const planYearParam = values.planYear
      ? `&planYear=${encodeURIComponent(values.planYear)}`
      : '';

    setGeneratedUrls({
      [ENV_LOCAL]: `${getBaseUrl(
        ENV_LOCAL
      )}/${GUEST_SUFFIX}?deeplink=${encodedParams}${planYearParam}`,
      [ENV_DEV]: `${getBaseUrl(
        ENV_DEV
      )}/${GUEST_SUFFIX}?deeplink=${encodedParams}${planYearParam}`,
      [ENV_INT]: `${getBaseUrl(
        ENV_INT
      )}/${GUEST_SUFFIX}?deeplink=${encodedParams}${planYearParam}`,
      [ENV_STAGE]: `${getBaseUrl(
        ENV_STAGE
      )}/${GUEST_SUFFIX}?deeplink=${encodedParams}${planYearParam}`,
      [ENV_UAT]: `${getBaseUrl(
        ENV_UAT
      )}/${GUEST_SUFFIX}?deeplink=${encodedParams}${planYearParam}`,
      [ENV_PROD]: `${getBaseUrl(
        ENV_PROD
      )}/${GUEST_SUFFIX}?deeplink=${encodedParams}${planYearParam}`,
    });
  };

  const handleCopy = (url: string, field: string) => {
    copy(url);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const handleReset = () => {
    form.reset();
    form.setValue('language', 'en'); // Reset to English as default
    setGeneratedUrls(null);
  };

  const handleOpenUrl = (url: string) => {
    window.open(url, '_blank');
  };

  const normalizePolicyNumber = (e) => {
    let policyNumber: string = e.target.value;
    if (
      policyNumber &&
      !isNaN(Number(policyNumber)) &&
      policyNumber.length < 7
    ) {
      policyNumber = policyNumber.padStart(7, '0');
    }

    form.setValue('policyNumber', policyNumber);
  };

  const theme = createTheme('uhc');

  return (
    <ThemeProvider theme={theme}>
      <MainContainer>
        <Heading
          color="$core.color.brand.100"
          level={1}
          size="xl"
          style={{ marginBottom: '24px', marginTop: '24px' }}
        >
          {HEADING_GUEST_DEEPLINK_GENERATOR}
        </Heading>

        <PageContainer>
          <InstructionsSection>
            <InstructionsTable />
          </InstructionsSection>
          <Section2Container>
            <FormSection>
              <DeeplinksBanner
                data-testid="deeplinks-banner"
                status="info"
                title={TITLE_DEEPLINK_FORM}
              >
                {BANNER_DEEPLINK_FORM_MESSAGE}
              </DeeplinksBanner>

              <FormProvider state={form}>
                <DeeplinkFormDemoProviderSearchContainer data-testid="deeplink-generator-form">
                  {/* Required Field */}
                  <DeeplinkFormStyledSelectInput
                    data-auto-testid="deeplink-language"
                    data-testid="deeplink-language"
                    label={LABEL_LANGUAGE}
                    model="language"
                    options={LANGUAGE_OPTIONS}
                    placeholder={PLACEHOLDER_SELECT_LANGUAGE}
                    validators={{
                      required: ERROR_LANGUAGE_REQUIRED,
                    }}
                  />

                  {/* Conditional Fields */}
                  <DeeplinkFormStyledSelectInput
                    data-auto-testid="deeplink-lob"
                    data-testid="deeplink-lob"
                    isSearchable
                    label={LABEL_LOB}
                    model="lob"
                    options={LOB_OPTIONS}
                    placeholder={PLACEHOLDER_SELECT_LOB}
                    validators={{
                      validate: (value) => {
                        if (isLobRequired() && !value) {
                          return ERROR_LOB_REQUIRED;
                        }
                        return true;
                      },
                    }}
                  />

                  <DeeplinkFormStyledSelectInput
                    data-auto-testid="deeplink-coverage-type"
                    data-testid="deeplink-coverage-type"
                    isSearchable
                    label={`${LABEL_COVERAGE_TYPE} ${
                      isCoverageTypeRequired() ? '*' : ''
                    }`}
                    model="coverageType"
                    options={COVERAGE_TYPE_OPTIONS}
                    placeholder={PLACEHOLDER_SELECT_COVERAGE_TYPE}
                    validators={{
                      validate: (value) => {
                        if (isCoverageTypeRequired() && !value) {
                          return ERROR_COVERAGE_TYPE_REQUIRED;
                        }
                        return true;
                      },
                    }}
                  />

                  <DeeplinkFormStyledTextInput
                    data-auto-testid="deeplink-reciprocity-id"
                    data-testid="deeplink-reciprocity-id"
                    label={`${LABEL_RECIPROCITY_ID} ${
                      isReciprocityIdRequired() ? '*' : ''
                    }`}
                    model="reciprocityId"
                    placeholder={PLACEHOLDER_RECIPROCITY_ID}
                    validators={{
                      validate: (value) => {
                        if (isReciprocityIdRequired() && !value) {
                          return ERROR_RECIPROCITY_ID_REQUIRED;
                        }
                        return true;
                      },
                    }}
                  />

                  {isMnrLobSelected() && (
                    <DeeplinkFormStyledTextInput
                      data-auto-testid="deeplink-mnr-reciprocity-id"
                      data-testid="deeplink-mnr-reciprocity-id"
                      label={`${LABEL_MNR_RECIPROCITY_ID} ${
                        isMnrReciprocityIdRequired() ? '*' : ''
                      }`}
                      model="mnrReciprocityId"
                      placeholder={PLACEHOLDER_MNR_RECIPROCITY_ID}
                      validators={{
                        validate: (value) => {
                          if (isMnrReciprocityIdRequired() && !value) {
                            return ERROR_MNR_RECIPROCITY_ID_REQUIRED;
                          }
                          return true;
                        },
                      }}
                    />
                  )}

                  <DeeplinkFormStyledTextInput
                    data-auto-testid="deeplink-client-name"
                    data-testid="deeplink-client-name"
                    label={LABEL_CLIENT_NAME}
                    model="clientName"
                    placeholder={PLACEHOLDER_CLIENT_NAME}
                    validators={{
                      validate: (value) => {
                        if (isClientNameRequired() && !value) {
                          return ERROR_CLIENT_NAME_REQUIRED;
                        }
                        return true;
                      },
                    }}
                  />

                  {/* Conditional/Optional Fields */}
                  <DeeplinkFormStyledTextInput
                    data-auto-testid="deeplink-plan-name"
                    data-testid="deeplink-plan-name"
                    label={`${LABEL_PLAN_NAME}${
                      isPlanNameRequired() ? ' *' : ''
                    }`}
                    model="planName"
                    placeholder={PLACEHOLDER_PLAN_NAME}
                    validators={{
                      validate: (value) => {
                        if (isPlanNameRequired() && !value) {
                          return ERROR_PLAN_NAME_REQUIRED;
                        }
                        return true;
                      },
                    }}
                  />

                  <DeeplinkFormStyledSelectInput
                    data-auto-testid="deeplink-custom-branding"
                    data-testid="deeplink-custom-branding"
                    label={LABEL_CUSTOM_BRANDING}
                    model="customBranding"
                    options={YES_NO_OPTIONS}
                    placeholder={PLACEHOLDER_SELECT}
                  />

                  <DeeplinkFormStyledTextInput
                    data-auto-testid="deeplink-policy-number"
                    data-testid="deeplink-policy-number"
                    label={LABEL_POLICY_NUMBER}
                    model="policyNumber"
                    onBlur={normalizePolicyNumber}
                    placeholder={PLACEHOLDER_POLICY_NUMBER}
                  />

                  <DeeplinkFormStyledTextInput
                    data-auto-testid="deeplink-fips-code"
                    data-testid="deeplink-fips-code"
                    label={LABEL_FIPS_CODE}
                    model="fipsCode"
                    placeholder={PLACEHOLDER_FIPS_CODE}
                  />

                  <DeeplinkFormStyledTextInput
                    data-auto-testid="deeplink-cluster-key"
                    data-testid="deeplink-cluster-key"
                    label={LABEL_CLUSTER_KEY}
                    model="clusterKey"
                    placeholder={PLACEHOLDER_CLUSTER_KEY}
                  />

                  <DeeplinkFormStyledTextInput
                    data-auto-testid="deeplink-portal-source"
                    data-testid="deeplink-portal-source"
                    label={LABEL_PORTAL}
                    model="portal"
                    placeholder={PLACEHOLDER_PORTAL}
                  />

                  <DeeplinkFormStyledTextInput
                    data-auto-testid="deeplink-pv-rc"
                    data-testid="deeplink-pv-rc"
                    label={LABEL_PV_RC}
                    model="pvrc"
                    placeholder={PLACEHOLDER_PV_RC}
                  />

                  <DeeplinkFormStyledSelectInput
                    data-auto-testid="deeplink-save-without-validation"
                    data-testid="deeplink-save-without-validation"
                    label={LABEL_SAVE_WITHOUT_VALIDATION}
                    model="saveWithoutPlanValidation"
                    options={YES_NO_OPTIONS}
                    placeholder={PLACEHOLDER_SELECT}
                  />

                  <DeeplinkFormStyledSelectInput
                    data-auto-testid="deeplink-show-signin"
                    data-testid="deeplink-show-signin"
                    label={LABEL_SHOW_SIGN_IN}
                    model="signin"
                    options={ENABLE_DISABLE_OPTIONS}
                    placeholder={PLACEHOLDER_SELECT}
                  />

                  <DeeplinkFormStyledTextInput
                    data-auto-testid="deeplink-plan-year"
                    data-testid="deeplink-plan-year"
                    label={LABEL_PLAN_YEAR}
                    model="planYear"
                    placeholder={PLACEHOLDER_PLAN_YEAR}
                  />

                  <DeeplinkFormStyledTextInput
                    data-auto-testid="deeplink-state-code"
                    data-testid="deeplink-state-code"
                    label={LABEL_STATE_CODE}
                    model="stateCode"
                    placeholder={PLACEHOLDER_STATE_CODE}
                  />

                  {Object.keys(form.formState.errors || {}).length > 0 && (
                    <ValidationSummary data-testid="validation-summary">
                      {ERROR_VALIDATION_SUMMARY}
                    </ValidationSummary>
                  )}

                  <ButtonContainer>
                    <StyledButton
                      data-testid="deeplink-generate-btn"
                      isDisabled={
                        Object.keys(form.formState.errors || {}).length > 0
                      }
                      onClick={handleGenerate}
                      variant="filled"
                    >
                      {BTN_GENERATE}
                    </StyledButton>
                    <Button
                      data-testid="deeplink-reset-btn"
                      onClick={handleReset}
                      variant="outline"
                    >
                      {BTN_RESET}
                    </Button>
                  </ButtonContainer>
                </DeeplinkFormDemoProviderSearchContainer>
              </FormProvider>
            </FormSection>

            <ExampleSection>
              <SampleDeeplinkTable />
            </ExampleSection>

            {generatedUrls && (
              <GeneratedUrlsWrapper>
                <UrlDisplayContainer data-testid="deeplink-urls">
                  <Heading color="$core.color.brand.100" level={2} size="lg">
                    {HEADING_GENERATED_DEEPLINK_URLS}
                  </Heading>
                  {window.location.hostname.toLowerCase() === 'localhost' && (
                    <UrlRow>
                      <UrlLabel>{ENV_LOCAL}</UrlLabel>
                      <UrlText
                        data-testid="deeplink-url-local"
                        readOnly
                        value={generatedUrls[ENV_LOCAL]}
                      />
                      <CopyButton
                        data-testid="deeplink-copy-local"
                        onClick={() =>
                          handleCopy(generatedUrls[ENV_LOCAL], ENV_LOCAL)
                        }
                        size="$sm"
                        variant="filled"
                      >
                        {copiedField === ENV_LOCAL ? BTN_COPIED : BTN_COPY}
                      </CopyButton>
                      <Button
                        data-testid="deeplink-open-local"
                        onClick={() => handleOpenUrl(generatedUrls[ENV_LOCAL])}
                        size="$sm"
                        variant="outline"
                      >
                        {BTN_OPEN}
                      </Button>
                    </UrlRow>
                  )}
                  <UrlRow>
                    <UrlLabel>{ENV_DEV}</UrlLabel>
                    <UrlText
                      data-testid="deeplink-url-dev"
                      readOnly
                      value={generatedUrls[ENV_DEV]}
                    />
                    <CopyButton
                      data-testid="deeplink-copy-dev"
                      onClick={() =>
                        handleCopy(generatedUrls[ENV_DEV], ENV_DEV)
                      }
                      size="$sm"
                      variant="filled"
                    >
                      {copiedField === ENV_DEV ? BTN_COPIED : BTN_COPY}
                    </CopyButton>
                    <Button
                      data-testid="deeplink-open-dev"
                      onClick={() => handleOpenUrl(generatedUrls[ENV_DEV])}
                      size="$sm"
                      variant="outline"
                    >
                      {BTN_OPEN}
                    </Button>
                  </UrlRow>
                  <UrlRow>
                    <UrlLabel>{ENV_INT}</UrlLabel>
                    <UrlText
                      data-testid="deeplink-url-int"
                      readOnly
                      value={generatedUrls[ENV_INT]}
                    />
                    <CopyButton
                      data-testid="deeplink-copy-int"
                      onClick={() =>
                        handleCopy(generatedUrls[ENV_INT], ENV_INT)
                      }
                      size="$sm"
                      variant="filled"
                    >
                      {copiedField === ENV_INT ? BTN_COPIED : BTN_COPY}
                    </CopyButton>
                    <Button
                      data-testid="deeplink-open-int"
                      onClick={() => handleOpenUrl(generatedUrls[ENV_INT])}
                      size="$sm"
                      variant="outline"
                    >
                      {BTN_OPEN}
                    </Button>
                  </UrlRow>
                  <UrlRow>
                    <UrlLabel>{ENV_STAGE}</UrlLabel>
                    <UrlText
                      data-testid="deeplink-url-stage"
                      readOnly
                      value={generatedUrls[ENV_STAGE]}
                    />
                    <CopyButton
                      data-testid="deeplink-copy-stage"
                      onClick={() =>
                        handleCopy(generatedUrls[ENV_STAGE], ENV_STAGE)
                      }
                      size="$sm"
                      variant="filled"
                    >
                      {copiedField === ENV_STAGE ? BTN_COPIED : BTN_COPY}
                    </CopyButton>
                    <Button
                      data-testid="deeplink-open-stage"
                      onClick={() => handleOpenUrl(generatedUrls[ENV_STAGE])}
                      size="$sm"
                      variant="outline"
                    >
                      {BTN_OPEN}
                    </Button>
                  </UrlRow>
                  <UrlRow>
                    <UrlLabel>{ENV_UAT}</UrlLabel>
                    <UrlText
                      data-testid="deeplink-url-uat"
                      readOnly
                      value={generatedUrls[ENV_UAT]}
                    />
                    <CopyButton
                      data-testid="deeplink-copy-uat"
                      onClick={() =>
                        handleCopy(generatedUrls[ENV_UAT], ENV_UAT)
                      }
                      size="$sm"
                      variant="filled"
                    >
                      {copiedField === ENV_UAT ? BTN_COPIED : BTN_COPY}
                    </CopyButton>
                    <Button
                      data-testid="deeplink-open-uat"
                      onClick={() => handleOpenUrl(generatedUrls[ENV_UAT])}
                      size="$sm"
                      variant="outline"
                    >
                      {BTN_OPEN}
                    </Button>
                  </UrlRow>
                  <UrlRow>
                    <UrlLabel>{ENV_PROD}</UrlLabel>
                    <UrlText
                      data-testid="deeplink-url-prod"
                      readOnly
                      value={generatedUrls[ENV_PROD]}
                    />
                    <CopyButton
                      data-testid="deeplink-copy-prod"
                      onClick={() =>
                        handleCopy(generatedUrls[ENV_PROD], ENV_PROD)
                      }
                      size="$sm"
                      variant="filled"
                    >
                      {copiedField === ENV_PROD ? BTN_COPIED : BTN_COPY}
                    </CopyButton>
                    <Button
                      data-testid="deeplink-open-prod"
                      onClick={() => handleOpenUrl(generatedUrls[ENV_PROD])}
                      size="$sm"
                      variant="outline"
                    >
                      {BTN_OPEN}
                    </Button>
                  </UrlRow>
                </UrlDisplayContainer>
              </GeneratedUrlsWrapper>
            )}
          </Section2Container>
        </PageContainer>
      </MainContainer>
    </ThemeProvider>
  );
};
